<template>
  <!--
    Card linking to /free-pt-session, shown only while a run is promoted
    (promoteFrom to end in src/data/freePtCampaign.js), and gone in the run's last
    second when the countdown emits `ended`. Every date, trainer and
    count comes from the run, so the next run needs a config change only.
    ?from= rather than utm_*: a UTM would overwrite the visitor's stored ad
    click (router/index.js captureAttribution).
  -->
  <aside v-if="run" class="free-pt-promo" :class="{ 'free-pt-promo--many': trainers.length > 3 }" :aria-label="`Free PT session at ${run.location}`">
    <div class="promo-inner">
      <div class="promo-top">
        <div class="promo-copy">
          <p class="promo-eyebrow">Free PT session · GYMPODS {{ run.location }}</p>
          <!-- A styled <p>, not a heading, so the card adds nothing to the page's heading outline. -->
          <p class="promo-title">Grab a PT <span class="promo-title-accent">for free</span></p>
          <p class="promo-line">45 minutes one-to-one with a Personal Trainer, in a private POD that's yours for the session.</p>
          <div class="promo-actions">
            <router-link :to="{ path: '/free-pt-session', query: { from: medium } }" class="promo-btn">
              Claim my free PT session <ArrowRight :size="16" aria-hidden="true" />
            </router-link>
            <FreePtCountdown :run="run" @ended="run = null" />
          </div>
        </div>

        <div class="promo-tiles">
          <router-link
            v-for="t in trainers"
            :key="t.key"
            :to="{ path: '/free-pt-session', query: { from: medium, trainer: t.key }, hash: '#claim' }"
            class="promo-tile"
            :aria-label="`Claim a free session with ${t.name}, ${tileLabelDays(t)}`"
          >
            <!--
              sizes is the widest tile at each viewport width (the card's container query sizes the tiles):
              (card - padding - gaps) / 3 up to 799px, 124px from 800px. Not "auto": Chrome resolves it
              before the app's re-created tile is laid out, and fetched a second, wider file.
            -->
            <picture v-if="t.promoPhoto">
              <source v-if="t.promoPhoto.avif" type="image/avif" :srcset="t.promoPhoto.avif" sizes="(max-width: 799px) calc((100vw - 60px) / 3), 124px" />
              <source v-if="t.promoPhoto.webp" type="image/webp" :srcset="t.promoPhoto.webp" sizes="(max-width: 799px) calc((100vw - 60px) / 3), 124px" />
              <!-- loading before src: the app re-creates the prerendered page, and a browser fetches a new img as soon as src is set unless loading="lazy" is already on it. -->
              <img loading="lazy" :src="t.promoPhoto.src" alt="" width="124" height="155" decoding="async" class="promo-tile-photo" />
            </picture>
            <span v-else class="promo-tile-photo promo-tile-photo--empty" aria-hidden="true">{{ t.name.charAt(0) }}</span>
            <span class="promo-tile-text">
              <span class="promo-tile-name">{{ t.name }}</span>
              <span class="promo-tile-day">{{ tileDay(t) }}</span>
            </span>
          </router-link>
        </div>
      </div>

      <div class="promo-strip">
        <p class="promo-lead">
          <span class="promo-dot" aria-hidden="true"></span>
          <span class="promo-lead-text">
            <strong>First come, first served.</strong>
            <span class="promo-count">Free sessions with {{ trainers.length }} Personal Trainer{{ trainers.length === 1 ? '' : 's' }}</span>
          </span>
        </p>
        <ul class="promo-facts">
          <li><CalendarDays :size="15" aria-hidden="true" />{{ dateRange }}</li>
          <li class="promo-narrow-only"><MapPin :size="15" aria-hidden="true" />{{ run.location }} only</li>
          <li><Tag :size="15" aria-hidden="true" />PT Intro normally <s>£{{ run.introPrice }}</s></li>
        </ul>
      </div>
    </div>
  </aside>
</template>

<script>
import { ArrowRight, CalendarDays, MapPin, Tag } from 'lucide-vue-next';
import FreePtCountdown from '@/components/FreePtCountdown.vue';
import { claimableTrainers, currentRun, datesText, isPromoted, slotStarted, ukDate } from '@/data/freePtCampaign.js';

export default {
  name: 'FreePtPromo',
  components: { FreePtCountdown, ArrowRight, CalendarDays, MapPin, Tag },
  props: {
    // Where the card sits, e.g. 'homepage' or 'mypods'. Sent as ?from= and saved with the claim.
    medium: { type: String, required: true },
  },
  data() {
    const now = Date.now();
    return { now, run: isPromoted(now) ? currentRun(now) : null };
  },
  computed: {
    // The landing page's format: "26 Oct to 28 Nov".
    dateRange() {
      const short = { day: 'numeric', month: 'short' };
      return `${ukDate(this.run.start, short)} to ${ukDate(this.run.end, short)}`;
    },
    // The trainers who can still be claimed, as on the landing page.
    trainers() { return claimableTrainers(this.run, this.now); },
  },
  methods: {
    // "Tuesday mornings" → "Tue mornings".
    shortDay(day) { return day.replace(/^([A-Z][a-z]{2})[a-z]*day\b/, '$1'); },
    // The trainer's `promoDay` ("Sat 21 Nov"); else one `dated` window: its dates left, "Sat 7 & 14 Nov";
    // one window: "Wed evenings"; two or more: each slot's `short`, "Tue & Fri".
    tileDay(t) {
      if (t.promoDay) return t.promoDay;
      if (t.slots.length === 1 && t.slots[0].dated) return `${t.slots[0].short} ${datesText(t.slots[0].dates, { month: 'short', and: '&' })}`;
      return t.slots.length === 1 ? this.shortDay(t.slots[0].day) : t.slots.map((s) => s.short).join(' & ');
    },
    // "Wednesday evenings", or "Tuesday mornings or Friday lunchtimes from 6 November" until a window starts.
    // Each window as its card reads it (`label`), so Matilda's is "Saturday 7 and 14 November".
    tileLabelDays(t) {
      return t.slots.map((s) => (slotStarted(s, this.now) ? s.label : `${s.label} from ${ukDate(s.from, { day: 'numeric', month: 'long' })}`)).join(' or ');
    },
  },
};
</script>

<style scoped lang="scss">
$aqua: #7fc0c3;
$charcoal: #231F20;
$zest: #D8F789;
$midnight: #193446;
$mist: mix($aqua, #fff, 10%);

// Lays out by its own width (container query), not the viewport, because it
// sits in sections of different widths. A container can't query itself, so
// the padding sits on .promo-inner.
.free-pt-promo {
  container: free-pt-promo / inline-size;
  border-radius: 20px;
  background: linear-gradient(135deg, $midnight 0%, darken($midnight, 4%) 100%);
  color: #fff;
  font-family: 'Manrope', sans-serif;
  text-align: left;

  // Host pages style bare p and ul; the card sets its own.
  p, ul { margin: 0; padding: 0; }
}

.promo-inner {
  display: flex;
  flex-direction: column;
  gap: 26px;
  padding: 36px 40px 26px;
}

.promo-top {
  display: flex;
  align-items: center;
  gap: 40px;
}

.promo-copy {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 14px;
}

.promo-eyebrow {
  font-size: 12px;
  font-weight: 700;
  line-height: 1.4;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: $aqua;
}

.promo-title {
  font-size: 42px;
  line-height: 1.02;
  font-weight: 700;
  letter-spacing: -0.03em;
  color: #fff;
}

// The landing page H1's .h1-accent.
.promo-title-accent {
  display: block;
  color: $zest;
  font-weight: 300;
  letter-spacing: -0.04em;
}

.free-pt-promo .promo-line {
  margin-top: 2px;
  max-width: 400px;
  font-size: 16px;
  line-height: 1.5;
  color: rgba(#fff, 0.82);
}

// The button and the countdown (FreePtCountdown): side by side, the countdown
// wrapping under the button when the row is too narrow for both.
.promo-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 16px;
  margin-top: 6px;
}

// The landing page's .btn--lime, white on hover.
.promo-btn,
.promo-btn:visited {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 48px;
  box-sizing: border-box;
  padding: 0 22px;
  border-radius: 999px;
  background: $zest;
  color: $charcoal;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.2;
  text-decoration: none;
  box-shadow: 0 10px 30px -10px rgba($zest, 0.55);
  transition: background-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;

  &:hover { background: #fff; color: $charcoal; transform: translateY(-1px); }
  &:focus-visible { outline: 2px solid #fff; outline-offset: 3px; }
}

.promo-tiles {
  flex-shrink: 0;
  display: flex;
  gap: 12px;
}

.promo-tile,
.promo-tile:visited {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 124px;
  color: #fff;
  text-decoration: none;

  &:hover .promo-tile-name { color: $zest; }
  &:focus-visible { outline: 2px solid #fff; outline-offset: 4px; border-radius: 14px; }
}

.promo-tile-photo {
  display: block;
  width: 124px;
  height: 155px;
  object-fit: cover;
  border-radius: 14px;

  // No promoPhoto in the config: the initial on mist, as on the landing page.
  &--empty {
    display: flex;
    align-items: center;
    justify-content: center;
    background: $mist;
    color: $midnight;
    font-size: 40px;
    font-weight: 700;
  }
}

.promo-tile-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.promo-tile-name {
  font-size: 15px;
  font-weight: 700;
  line-height: 1.3;
  transition: color 0.2s ease;
}

.promo-tile-day {
  font-size: 12.5px;
  line-height: 1.3;
  color: rgba(#fff, 0.7);
}

.promo-strip {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px 24px;
  padding-top: 20px;
  border-top: 1px solid rgba(#fff, 0.14);
}

.promo-lead {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 15px;
  line-height: 1.4;
}

.promo-dot {
  flex-shrink: 0;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: $zest;
  box-shadow: 0 0 0 5px rgba($zest, 0.18);
}

.promo-lead-text {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 2px 12px;

  strong { font-weight: 800; color: $zest; }
}

.promo-count { color: rgba(#fff, 0.82); }

.promo-facts {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 20px;
  list-style: none;
  font-size: 13px;
  line-height: 1.4;
  color: rgba(#fff, 0.78);

  li {
    display: flex;
    align-items: center;
    gap: 7px;
    margin: 0;
  }

  svg { flex-shrink: 0; color: $aqua; }
  s { margin-left: -3px; }
}

// Narrow-only extras. Beats .promo-facts li.
.free-pt-promo .promo-narrow-only { display: none; }

// Four or more trainers: slimmer tiles beside the copy on a wide card.
.free-pt-promo--many {
  .promo-tile,
  .promo-tile:visited { width: 104px; }

  .promo-tile-photo { width: 104px; height: 130px; }
}

// Four or more trainers don't fit beside the copy below 1000px: the tiles go on top, all in one row,
// as on the narrow card. The narrow block below takes over from 699px.
@container free-pt-promo (max-width: 999px) {
  .free-pt-promo--many {
    .promo-top {
      flex-direction: column;
      align-items: stretch;
      gap: 24px;
    }

    .promo-tiles {
      order: -1;
      display: grid;
      grid-auto-flow: column;
      grid-auto-columns: minmax(0, 1fr);
      gap: 10px;
    }

    .promo-tile,
    .promo-tile:visited { width: auto; gap: 8px; }

    .promo-tile-photo {
      width: 100%;
      height: auto;
      aspect-ratio: 4 / 5;
      max-height: 180px;
      object-position: center 30%;
      border-radius: 12px;
    }
  }
}

// Mid-width card (narrowed desktop window, landscape tablet): keep the side-by-side layout with
// smaller tiles instead of dropping to the phone stack, which left dead space beside the photos
// and stretched the button across the card. The narrow block below overrides all of this.
@container free-pt-promo (max-width: 819px) {
  .promo-inner {
    gap: 24px;
    padding: 32px 32px 24px;
  }

  .promo-top { gap: 28px; }
  .promo-tiles { gap: 10px; }

  .promo-tile,
  .promo-tile:visited {
    width: 100px;
    gap: 8px;
  }

  .promo-tile-photo {
    width: 100px;
    height: 125px;
    border-radius: 12px;
  }

  .promo-tile-name { font-size: 14px; }
  .promo-tile-day { font-size: 12px; }
  .promo-title { font-size: 40px; }
}

// Narrow card (phones, portrait tablets): tiles first, then the copy, the button, and the strip stacked.
@container free-pt-promo (max-width: 699px) {
  .promo-inner {
    gap: 22px;
    padding: 22px 20px 24px;
  }

  .promo-top {
    flex-direction: column;
    align-items: stretch;
    gap: 22px;
  }

  // Three equal tiles across the full width, so there's no dead space beside them.
  .promo-tiles {
    order: -1;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 10px;
  }

  .promo-tile,
  .promo-tile:visited {
    width: auto;
    gap: 8px;
  }

  // 4:5 like the wide tiles, capped at 180px tall so tablets don't get huge photos.
  .promo-tile-photo {
    width: 100%;
    height: auto;
    aspect-ratio: 4 / 5;
    max-height: 180px;
    object-position: center 30%;
    border-radius: 12px;
  }

  .promo-tile-text { gap: 1px; }
  .promo-tile-name { font-size: 14px; }
  .promo-tile-day { font-size: 12px; }

  // Four or more trainers: three to a row, the last row centred (five as 3 + 2, four as 3 + 1).
  .free-pt-promo--many .promo-tiles {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    row-gap: 14px;
  }

  .free-pt-promo--many .promo-tile,
  .free-pt-promo--many .promo-tile:visited { flex: 0 0 calc((100% - 20px) / 3); }

  .promo-copy { gap: 12px; }
  .promo-eyebrow { font-size: 11.5px; }
  .promo-title { font-size: 36px; }
  .free-pt-promo .promo-line { max-width: none; font-size: 15px; }

  .promo-actions { margin-top: 10px; }

  .promo-btn,
  .promo-btn:visited { min-height: 50px; }

  .promo-strip {
    flex-direction: column;
    align-items: flex-start;
    gap: 14px;
    padding-top: 18px;
  }

  .promo-lead { align-items: flex-start; }
  .promo-dot { margin-top: 6px; }

  .promo-lead-text {
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;

    strong { font-size: 16px; }
  }

  .promo-count { font-size: 14px; }
  .promo-facts { gap: 8px 18px; }
  .free-pt-promo .promo-narrow-only { display: inline; }
  .promo-facts li.promo-narrow-only { display: flex; }
}

// Phones: full-width button with the countdown box under it. From 480px the button keeps its
// natural size, as on the wide card, with the countdown beside it (or wrapped under it).
@container free-pt-promo (max-width: 479.98px) {
  .promo-actions {
    flex-direction: column;
    align-items: stretch;
    align-self: stretch;
    gap: 10px;
  }
}
</style>
